const CHAT_STREAM_REVEAL_TICK_MS = 24;

export type ChatStreamStateHost = {
  chatStream: string | null;
  chatStreamTarget: string | null;
  chatStreamStartedAt: number | null;
  chatStreamRevealTimer: number | null;
};

export type ChatStreamSegmentHost = ChatStreamStateHost & {
  chatStreamSegments: Array<{ text: string; ts: number }>;
};

function clearChatStreamRevealTimer(host: ChatStreamStateHost) {
  if (host.chatStreamRevealTimer != null) {
    clearTimeout(host.chatStreamRevealTimer);
    host.chatStreamRevealTimer = null;
  }
}

function resolveRevealStep(backlog: number) {
  if (backlog >= 240) {
    return 48;
  }
  if (backlog >= 120) {
    return 24;
  }
  if (backlog >= 48) {
    return 12;
  }
  if (backlog >= 18) {
    return 6;
  }
  if (backlog >= 6) {
    return 3;
  }
  return 1;
}

function revealChatStream(host: ChatStreamStateHost) {
  host.chatStreamRevealTimer = null;
  const target = host.chatStreamTarget;
  const current = host.chatStream;
  if (target == null || current == null) {
    return;
  }
  if (current.length >= target.length) {
    if (current !== target) {
      host.chatStream = target;
    }
    return;
  }
  const nextLength = Math.min(target.length, current.length + resolveRevealStep(target.length - current.length));
  host.chatStream = target.slice(0, nextLength);
  if (nextLength < target.length) {
    scheduleChatStreamReveal(host);
  }
}

function scheduleChatStreamReveal(host: ChatStreamStateHost) {
  if (host.chatStreamRevealTimer != null) {
    return;
  }
  host.chatStreamRevealTimer = window.setTimeout(() => revealChatStream(host), CHAT_STREAM_REVEAL_TICK_MS);
}

export function initializeChatStream(host: ChatStreamStateHost, startedAt: number) {
  clearChatStreamRevealTimer(host);
  host.chatStream = "";
  host.chatStreamTarget = "";
  host.chatStreamStartedAt = startedAt;
}

export function getChatStreamAuthoritativeText(host: ChatStreamStateHost) {
  return host.chatStreamTarget ?? host.chatStream ?? "";
}

export function updateChatStreamTarget(host: ChatStreamStateHost, target: string) {
  if (host.chatStream == null) {
    host.chatStream = "";
  }
  host.chatStreamTarget = target;
  if (host.chatStream.length >= target.length) {
    host.chatStream = target;
    clearChatStreamRevealTimer(host);
    return;
  }
  scheduleChatStreamReveal(host);
}

export function flushChatStream(host: ChatStreamStateHost) {
  clearChatStreamRevealTimer(host);
  if (host.chatStreamTarget != null) {
    host.chatStream = host.chatStreamTarget;
  }
  return host.chatStream;
}

export function resetChatStream(host: ChatStreamStateHost) {
  clearChatStreamRevealTimer(host);
  host.chatStream = null;
  host.chatStreamTarget = null;
  host.chatStreamStartedAt = null;
}

export function commitChatStreamSegment(host: ChatStreamSegmentHost, ts = Date.now()) {
  const text = flushChatStream(host);
  if (typeof text === "string" && text.trim().length > 0) {
    host.chatStreamSegments = [...host.chatStreamSegments, { text, ts }];
  }
  resetChatStream(host);
  return text;
}
